// Shared antd-form-builder widget registration for the schema form engine.
//
// antd-form-builder's FormBuilder.defineWidget THROWS if a widget name is already
// defined. The preview (PreviewFormModal) and the runtime (SchemaFormRenderer) both
// need the same string widgets, and in the SPA every route module executes its
// top-level code at startup — so registering in each file made whichever loaded
// second crash ("Widget 'fixed_image' already defined.").
//
// Centralising here means the defineWidget calls run EXACTLY ONCE (ES module
// singleton). The try/catch is defence-in-depth for dev hot-reload, which can
// re-execute module code. Import this module for its side effect from anywhere
// that renders schema_json:  import '@iso/library/helpers/schemaFormWidgets';
import React, { useRef } from 'react';
import { Input, Rate, Upload, Button, Select, AutoComplete } from 'antd';
import { UploadOutlined, DeleteOutlined, FilePdfOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import FormBuilder from 'antd-form-builder';
import LocationPicker from '@iso/components/LocationPicker/LocationPicker';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// File Manager stores a path (`/uploads/attachments/...`). Used as an href or
// iframe src that path is resolved against the page, which is the frontend, and
// the SPA answers with its own 404. Absolute URLs (S3, a pasted https link)
// are already a host and stay as they are.
export const resolveStoredFileUrl = (url) => {
  if (!url) {
    return '';
  }
  const value = String(url);
  if (/^(https?:|data:|blob:)/i.test(value)) {
    return value;
  }
  const base = API_BASE.replace(/\/+$/, '');
  const path = value.startsWith('/') ? value : `/${value}`;
  return `${base}${path}`;
};

const defineWidgetOnce = (key, widget) => {
  try {
    FormBuilder.defineWidget(key, widget);
  } catch (e) {
    // Already defined (module re-execution / duplicate import) — first wins.
  }
};

// Read/display widgets.
const DateView = ({ value }) => (value && value.format ? value.format('MMM Do YYYY') : 'N/A');
const FixedTextWidget = ({ content }) => <div style={{ whiteSpace: 'pre-wrap' }}>{content || ''}</div>;
const FixedImageWidget = ({ content }) =>
  (content ? <img src={resolveStoredFileUrl(content)} alt="" style={{ maxWidth: '100%' }} /> : <div style={{ color: '#999' }}>[No image]</div>);

// Signature: a real, writable signature pad so the Preview shows exactly what the
// field is — the user can draw on it. (The interactive runtime, SchemaFormRenderer,
// still overrides this per-field with the full submission-capable SignaturePad; this
// widget is what the read-only Preview renders.)
const SignaturePadWidget = () => {
  const sigRef = useRef(null);
  return (
    <div>
      <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff', display: 'inline-block', width: '100%', maxWidth: 420 }}>
        <SignatureCanvas
          ref={sigRef}
          penColor="black"
          canvasProps={{ width: 420, height: 150, style: { width: '100%', height: 150, borderRadius: 4 } }}
        />
      </div>
      <div>
        <Button type="link" size="small" icon={<DeleteOutlined />} onClick={() => sigRef.current && sigRef.current.clear()}>
          Clear
        </Button>
      </div>
    </div>
  );
};
const UploadPlaceholder = () => (
  <Upload beforeUpload={() => false} maxCount={1}>
    <Button icon={<UploadOutlined />}>Upload file</Button>
  </Upload>
);

// 'List Hosts' (host-picker): a single-select of the property's hosts, each shown
// by its Display Label (host_display). The property's hosts aren't available in
// the library/global preview, so here it renders as a disabled placeholder select.
// The interactive runtime (SchemaFormRenderer) REMAPS host-picker -> a real
// 'select' populated with { value: host.id, label: host.host_display } before
// this widget would ever be used, so this placeholder is preview-only.
const HostPickerPlaceholder = (props) => (
  <Select
    {...props}
    disabled
    style={{ width: '100%', ...(props && props.style) }}
    placeholder="Select host (populated from the property's hosts)"
  />
);

// 'Property Area / Location' (area-picker): an autocomplete over the property's
// Areas that still accepts anything typed. It is a SUGGESTION list, not a
// constraint — a contractor is regularly sent somewhere that was never drawn on
// the map, and a strict picker would make the field unanswerable — so the value
// submitted is always the text, never an area id.
//
// The property's areas aren't available in the library/global preview, so here
// it renders as a plain input with a hint. The runtime (SchemaFormRenderer)
// swaps in a real AutoComplete populated from the property before this would be
// used, exactly as host-picker does.
const AreaPickerPlaceholder = (props) => (
  <AutoComplete
    {...props}
    options={[]}
    style={{ width: '100%', ...(props && props.style) }}
    placeholder="Type, or choose one of the property's areas"
  />
);

// 'Fixed PDF' (fixed_pdf): a document the author attached for the person filling
// the form to read — a safety data sheet, a site map, a policy.
//
// Shown INLINE, so a contractor reads it where they are instead of leaving the
// form to open a tab and finding their way back. The same plain <iframe> the
// File Manager and Safety Documents previews use, against the same storage —
// which is the evidence that these files are served frame-able rather than as
// forced downloads.
//
// `loading="lazy"`: a step can carry several documents, and fetching every PDF
// the moment the step renders would stall a phone on a gate signal before the
// person has scrolled to any of them.
//
// The name and the View / Download actions stay above the preview. A frame is
// a poor way to read anything long, and View hands the file to the browser's
// own full-window reader.
const FixedPdfWidget = ({ content, contentName, contentLabel }) => {
  if (!content) {
    return <div style={{ color: '#999' }}>[No PDF]</div>;
  }
  const name = contentName || contentLabel || 'Document';
  const src = resolveStoredFileUrl(content);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        border: '1px solid #eaecf0',
        borderRadius: 8,
        background: '#fcfcfd',
        flexWrap: 'wrap',
      }}
    >
      <FilePdfOutlined style={{ fontSize: 22, color: '#b42318' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {contentLabel && <div style={{ fontWeight: 500 }}>{contentLabel}</div>}
        <div style={{ color: '#667085', fontSize: 12, wordBreak: 'break-word' }}>{name}</div>
      </div>
      {/* "Full screen", not "View" — the document is already on screen below,
          so this button's job is the browser's own reader, which is where
          anything longer than a page or two is actually readable.
          rel=noopener: this opens a URL the form author supplied. */}
      <Button
        size="small"
        icon={<EyeOutlined />}
        href={src}
        target="_blank"
        rel="noopener noreferrer"
      >
        Full screen
      </Button>
      <Button size="small" icon={<DownloadOutlined />} href={src} download={name}>
        Download
      </Button>
      <iframe
        src={src}
        title={name}
        loading="lazy"
        style={{
          // Full width of the field, and a height that shows a page without
          // swallowing the step it sits in.
          width: '100%',
          height: 480,
          border: '1px solid #eaecf0',
          borderRadius: 6,
          background: '#fff',
          // Its own line under the header row, whatever the flex layout above
          // is doing.
          flexBasis: '100%',
        }}
      />
    </div>
  );
};

// A section rule. Not an author-selectable widget: it is produced by the
// per-field "Add horizontal line at end" option (withRowDividers), which is why
// it is absent from LIBRARY_FIELD_WIDGETS. Collects no value.
const DividerWidget = () => (
  <hr
    style={{
      border: 0,
      borderTop: '1px solid #eaecf0',
      margin: '4px 0 20px',
      width: '100%',
    }}
  />
);

// 'text' is our label for the default input; antd-form-builder has no built-in
// 'text' widget (it uses Input only when `widget` is absent), so alias it here so
// imported schemas with `widget: 'text'` render.
defineWidgetOnce('text', Input);
defineWidgetOnce('date-view', DateView);
defineWidgetOnce('fixed_text', FixedTextWidget);
defineWidgetOnce('fixed_image', FixedImageWidget);
defineWidgetOnce('scale', Rate);
defineWidgetOnce('signature', SignaturePadWidget);
defineWidgetOnce('upload_file', UploadPlaceholder);
defineWidgetOnce('host-picker', HostPickerPlaceholder);
defineWidgetOnce('area-picker', AreaPickerPlaceholder);
defineWidgetOnce('divider', DividerWidget);
defineWidgetOnce('fixed_pdf', FixedPdfWidget);
defineWidgetOnce('location', LocationPicker);

export {};
